// Fonts from the server's shared font library: loaded on startup, added by upload.
import { invalidateTextMetrics } from '@vellum/core';
import { app } from './app.ts';
import { addFeatureSource } from './fontFeatures.ts';
import { fontsChanged } from './shaping.ts';

export interface LibraryFont {
  id: string;
  family: string;
  style: string;
  weight: number;
  italic: boolean;
  url: string;
  /** The font as plain OpenType, for the shaper */
  sfnt?: string;
  uploadedBy: string;
}

export const libraryFonts: LibraryFont[] = [];
const loaded = new Set<string>();

async function install(fonts: LibraryFont[]) {
  const fresh = fonts.filter((f) => !loaded.has(f.id));
  if (!fresh.length) return;
  await Promise.all(
    fresh.map(async (f) => {
      loaded.add(f.id);
      libraryFonts.push(f);
      addFeatureSource(f.family, f.weight, f.italic, f.url);
      try {
        const face = new FontFace(f.family, `url(${f.url})`, { weight: String(f.weight), style: f.italic ? 'italic' : 'normal' });
        document.fonts.add(await face.load());
      } catch (e) {
        console.warn(`[vellum] font ${f.family} ${f.style} failed to load`, e);
      }
    }),
  );
  // text measured with a fallback font must be laid out again
  fontsChanged();
  invalidateTextMetrics();
  app.fontsReady();
  app.emit('ui');
}

export async function loadLibraryFonts() {
  try {
    const r = await fetch('/api/fonts');
    if (!r.ok) return;
    await install(((await r.json()) as { fonts: LibraryFont[] }).fonts);
  } catch {
    // offline or signed out: the bundled fonts still work
  }
}

/** Upload font files (TTF, OTF, WOFF, WOFF2). Returns the families added. */
export async function uploadFonts(files: FileList | File[]): Promise<string[]> {
  const added: LibraryFont[] = [];
  for (const file of files) {
    const r = await fetch('/api/fonts', { method: 'POST', body: file, headers: { 'content-type': 'application/octet-stream' } });
    const body = (await r.json()) as { font?: LibraryFont; error?: string };
    if (!r.ok || !body.font) {
      app.toast(`${file.name}: ${body.error ?? 'upload failed'}`);
      continue;
    }
    added.push(body.font);
  }
  await install(added);
  const families = [...new Set(added.map((f) => f.family))];
  if (families.length) app.toast(`Added ${families.join(', ')}`);
  return families;
}

/** Families for the font picker: uploaded ones first, then the built-in list. */
export function libraryFamilies(): string[] {
  return [...new Set(libraryFonts.map((f) => f.family))].sort();
}
